import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Form from "react-bootstrap/Form";
import Sidebar from "../../components/Sidebar/Sidebar";
import { Topbar } from "../../components/Topbar/Topbar";
import OptOutMarketingModal from "../../components/Modal/OptOutMarketingModal";
import axiosClient from "../../axiosClient";
import { useUser } from "../../context/UserContext";
import "./SettingsPage.scss";

const SettingsPage = () => {
  const { user } = useUser();
  const [marketingEmailsOptIn, setMarketingEmailsOptIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showOptOutModal, setShowOptOutModal] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadPreference = async () => {
      try {
        const response = await axiosClient.get("/auth/marketing-emails");
        if (!cancelled) {
          setMarketingEmailsOptIn(Boolean(response.data.marketingEmailsOptIn));
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error("Couldn't load your email preferences. Please try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadPreference();
    return () => {
      cancelled = true;
    };
  }, []);

  const savePreference = async (nextValue: boolean) => {
    setIsSaving(true);
    try {
      const response = await axiosClient.patch("/auth/marketing-emails", {
        marketingEmailsOptIn: nextValue,
      });
      setMarketingEmailsOptIn(Boolean(response.data.marketingEmailsOptIn));
      toast.success(
        nextValue
          ? "You're opted in to marketing emails."
          : "You've been opted out of marketing emails.",
      );
      setShowOptOutModal(false);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't update your preference. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleChange = (checked: boolean) => {
    if (!checked && marketingEmailsOptIn) {
      setShowOptOutModal(true);
      return;
    }
    void savePreference(checked);
  };

  return (
    <div className="page-wrapper" id="SettingsPage">
      <Sidebar />
      <div className="internal-page-wrapper">
        <Topbar title="Settings" />
        <main>
          <div className="settings-page-content">
            <section className="settings-section">
              <h2 className="settings-section__title">Email preferences</h2>
              <p className="settings-section__intro">
                Choose whether we can email you about promotions, discounts,
                product updates and news. We won&apos;t spam you, and you can
                unsubscribe later.
              </p>

              {isLoading ? (
                <p className="settings-section__loading">Loading preferences…</p>
              ) : (
                <Form.Check
                  type="switch"
                  id="marketing-emails-opt-in"
                  className="settings-marketing-switch"
                  label="I agree to receive emails about promotions, discounts, product updates and news"
                  checked={marketingEmailsOptIn}
                  disabled={isSaving}
                  onChange={(e) => handleToggleChange(e.target.checked)}
                />
              )}

              {user?.email ? (
                <p className="settings-section__meta">
                  Emails will go to <strong>{user.email}</strong>
                </p>
              ) : null}
            </section>
          </div>
        </main>
      </div>

      <OptOutMarketingModal
        show={showOptOutModal}
        onHide={() => setShowOptOutModal(false)}
        onConfirm={() => savePreference(false)}
        isSaving={isSaving}
      />
    </div>
  );
};

export default SettingsPage;
